<template>
  <UiBaseFormVee
    submitOnCtrlEnter
    :errorText="errorText"
    :onSubmit="onSubmit">
    <UiInputTextFieldVee
      name="name"
      :label="$t('term.name')"
      :rules="['required']"
      isRequired
      v-model="form.name" />

    <UiComboboxVee
      name="currencyCode"
      :label="$t('term.currency')"
      :rules="['required']"
      isRequired
      v-model="form.currencyCode"
      :options="currencyOptions" />

    <UiInputSelectVee
      name="pricingSegmentID"
      :label="$t('area.customer-account.field.pricing-segment')"
      :rules="['required']"
      isRequired
      v-model="form.pricingSegmentID"
      :options="segmentOptions" />

    <template #buttons>
      <UiBaseButton
        :text="$t('action.create')"
        :isLoading="isLoading"
        type="submit" />
    </template>
  </UiBaseFormVee>
</template>

<script lang="ts">
import { useCustomerAccountStore } from '@/stores/customerAccountStore'
import { usePricingSegmentStore } from '@/stores/pricingSegmentStore'
import { useModalStore } from '@/stores/modalStore'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'
import { useAppStore } from '@/stores/appStore'
import { useAccountStore } from '@/stores/accountStore'
import { useToast } from '@/globals/typescript/utils/toast'
import type { TLabManagementCurrencyCode } from '@team-milva/lab-models/currency'
import { mapActions, mapState } from 'pinia'
import type { PropType } from 'vue'

export default {
  name: 'CreateCustomerAccountForm',

  props: {
    onSuccess: {
      type: Function as PropType<(id: string) => void>,
      required: false,
      default: undefined,
    },
  },

  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      form: {
        name: '',
        currencyCode: '' as TLabManagementCurrencyCode | '',
        pricingSegmentID: '',
      },
      isLoading: false,
      errorText: '',
    }
  },

  computed: {
    ...mapState(usePricingSegmentStore, ['allPricingSegments']),
    ...mapState(useAppStore, ['currentLocale']),
    ...mapState(useAccountStore, ['availableCurrencies', 'defaultCurrencyCode']),

    segmentOptions() {
      return this.allPricingSegments
        .filter(
          (x) =>
            x.activeState === 'active' &&
            x.currencyCode === this.form.currencyCode
        )
        .map((pricingSegment) => ({
          value: pricingSegment.id,
          text: pricingSegment.getLabel(this.currentLocale),
        }))
    },

    currencyOptions(): { value: TLabManagementCurrencyCode; text: string }[] {
      return this.availableCurrencies.map((currency) => ({
        value: currency.code as TLabManagementCurrencyCode,
        text: `${currency.name[this.currentLocale] || currency.name['en']} (${currency.code})`,
      }))
    },
  },

  watch: {
    'form.name'() {
      this.resetValidationMessages()
    },
    'form.currencyCode'(newVal: TLabManagementCurrencyCode) {
      const currentSegment = this.allPricingSegments.find(
        (x) => x.id === this.form.pricingSegmentID
      )
      if (currentSegment && currentSegment.currencyCode !== newVal) {
        this.form.pricingSegmentID = ''
      }
      this.resetValidationMessages()
    },
    'form.pricingSegmentID'() {
      this.resetValidationMessages()
    },
  },

  methods: {
    ...mapActions(useCustomerAccountStore, ['createCustomerAccount']),
    ...mapActions(useModalStore, ['closeTop']),

    resetValidationMessages(): void {
      this.errorText = ''
    },
    async onSubmit(): Promise<void> {
      this.isLoading = true
      this.errorText = ''
      try {
        const customerAccountID = await this.createCustomerAccount({
          name: this.form.name,
          currencyCode: this.form.currencyCode as TLabManagementCurrencyCode,
          pricingSegmentID: this.form.pricingSegmentID,
        })
        this.unsavedGuard.takeSnapshot()
        useToast().success(this.$t('W: Kunde oprettet'))
        this.closeTop()
        this.onSuccess?.(customerAccountID)
      } catch {
        this.errorText = this.$t('error.common.standard') as string
        useToast().error(this.errorText)
      } finally {
        this.isLoading = false
      }
    },
  },

  created() {
    this.form.currencyCode = this.defaultCurrencyCode

    // Registered per child form rather than once on CreateCustomerModal, so each
    // form's own `form` object is its own snapshot — the same shape every other
    // guarded surface uses. Only one child is mounted at a time, so the modal's
    // own id is enough for ModalHost and for the pill switch to consult.
    //
    // Must come after the currencyCode seed above, or the baseline holds an empty
    // currency and the form reads dirty the moment it opens.
    this.unsavedGuard.register({
      snapshot: () => ({ ...this.form }),
      modalId: useModalStore().top?.id,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>
